import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Autocomplete, Box, Chip, TextField, Tooltip } from "@mui/material";
import { forwardRef, useMemo } from "react";
import { MetadataInputProps } from "../metadata-input-index";
import { UserHasAcl, DistinctByProp } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { CreateNewDbMetadataValue } from "../../../Core/Utils/asset-object-utils";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { DbMetadataFieldValueInfos } from "../../../Core/Api/Types/db-metadata-field-value-infos";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { useTranslation } from "react-i18next";

const MetadataInputStringMulti = forwardRef<any, MetadataInputProps>((props, ref) => {
  const { t } = useTranslation();
  const { onChange, data, value, predefinedValues } = props;

  const userCanAddValue = useMemo(
    () =>
      (data.allowPopulate as Boolean) &&
      (UserHasAcl(AclTypes.aclMetadataFieldPopulate) || UserHasAcl(AclTypes.aclSuperUser)),
    []
  );

  const getValue = () => {
    if (!data.values || data.values.length <= 0 || !value) return [];

    if (userCanAddValue && Array.isArray(value))
      return value.map(
        (x) =>
          data.values?.find((y) => y.value === x) ??
          CreateNewDbMetadataValue(-500, BaseMetadataType.DbStringMetadataValueInfos, "", x.value ?? x)
      );

    if (userCanAddValue && Array.isArray(value.value))
      return value.value.map(
        (x) =>
          data.values?.find((y) => y.value === x) ??
          CreateNewDbMetadataValue(-500, BaseMetadataType.DbStringMetadataValueInfos, "", x.value ?? x)
      );

    if (Array.isArray(value)) return DistinctByProp(data.values, "value").filter((x) => value.includes(x.value));

    if (!value.value) return [];

    if (Array.isArray(value.value))
      return DistinctByProp(data.values, "value").filter(
        (x) => value.value.includes(x.value) || value.value.includes(parseInt(x.value))
      );
    return [];
  };

  return (
    <>
      {(data.values && data.values.length > 0) ||
      data.baseType === BaseMetadataType.DbAnyObjectMultiMetadataValueInfos ? (
        <Autocomplete
          multiple={true}
          sx={{ mt: -3, position: "relative" }}
          onChange={(e, value) =>
            onChange(
              value.length > 0
                ? value.map(
                    (x) =>
                      new DbMetadataFieldValueInfos({
                        externalId: -500,
                        id: x.id ?? -500,
                        value: x.value ?? x,
                        label: x.label ?? x.value ?? x,
                        valueObject: CreateNewDbMetadataValue(
                          -500,
                          BaseMetadataType.DbStringMetadataValueInfos,
                          "",
                          x.value ?? x
                        ),
                      })
                  )
                : null,
              data.id
            )
          }
          options={DistinctByProp(
            data.values
              ?.filter((x) => x.label != null && x.label != "")
              .sort((a, b) => {
                if ((a.label ?? a.value ?? a ?? "") < (b.label ?? b.value ?? b ?? "")) return -1;
                if ((a.label ?? a.value ?? a ?? "") > (b.label ?? b.value ?? b ?? "")) return 1;
                return 0;
              }) ?? [],
            "value"
          )} // Do that to avois multiple same keys
          getOptionLabel={(value: any) => value.label ?? value.value ?? value}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterOptions={(x, s) => {
            if (!s || !s.inputValue) return x;
            return x.filter((a) => a.value?.includesAllWords(s.inputValue) || a.label?.includesAllWords(s.inputValue));
          }}
          renderOption={(props, option) => {
            return (
              <Box {...props} key={option.id} component="li">
                {option.label}
              </Box>
            );
          }}
          renderTags={(value) =>
            value.map((option) => (
              <Chip
                variant="outlined"
                label={option.label ?? option.value ?? option}
                sx={{ m: 0.5 }}
                color={
                  data.values!.find((x) => x.value === option.value || x.value === option) === undefined
                    ? "warning"
                    : "default"
                }
                onDelete={() =>
                  onChange(
                    value.filter((item) => item.id !== option.id),
                    data.id
                  )
                }
              />
            ))
          }
          freeSolo={userCanAddValue}
          renderInput={(params) => (
            <>
              {userCanAddValue && (
                <Tooltip title={t("tooltip:title.userCanAddValue", { context: "pressEnter" })}>
                  <QuestionMarkIcon
                    sx={{ zIndex: 700, position: "absolute", right: 4, bottom: 4, opacity: 0.25 }}
                    fontSize="small"
                  />
                </Tooltip>
              )}
              <TextField {...params} />
            </>
          )}
          disableClearable={predefinedValues}
          value={getValue()}
        />
      ) : (
        <Autocomplete
          id={data.tagName!}
          // options management
          options={[]}
          isOptionEqualToValue={(option, value) => option === value}
          // value management
          value={value ? value.value ?? value : []}
          onChange={(e, value) => {
            onChange(value.length > 0 ? value : null, data.id);
          }}
          // render input
          multiple
          freeSolo
          popupIcon={<ExpandMoreRoundedIcon />}
          renderInput={(params) => (
            <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("field:placeholder.typeValuesAndPressEnter")} />
          )}
          sx={{ width: "100%" }}
        />
      )}
    </>
  );
});

export default MetadataInputStringMulti;
